import { Card } from '../ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue, SelectGroup, SelectLabel } from '../ui/select';
import { User, Users, MoreVertical, Edit2, Trash2 } from 'lucide-react';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../ui/dropdown-menu';

export function EmployeeList({ 
  employees, 
  selectedEmployee, 
  onSelectEmployee, 
  onEditEmployee, 
  onDeleteEmployee, 
  isApprover,
  employeeSalaryData
}) {
  // Separate active and previous employees
  const activeEmployees = employees.filter(emp => !emp.end_date);
  const previousEmployees = employees.filter(emp => emp.end_date);
  
  // Handle dropdown selection
  const handleSelectEmployee = (employeeId) => {
    const emp = employees.find(e => e.employee_id === employeeId);
    if (emp) {
      onSelectEmployee(emp);
    }
  };
  
  const renderSelectedEmployeeCard = () => {
    if (!selectedEmployee) return null;
    
    const salaryInfo = employeeSalaryData?.[selectedEmployee.employee_id];
    const isPrevious = !!selectedEmployee.end_date;
    
    return (
      <div
        className="relative group rounded-xl transition-all duration-200 bg-gradient-to-r from-slate-800 to-slate-900 text-white shadow-lg shadow-slate-900/30"
      >
        <div className="w-full text-left p-4">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0 bg-gradient-to-br from-amber-400 to-amber-600">
              <User className="w-5 h-5 text-white" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-semibold truncate">{selectedEmployee.name}</div>
              <div className="text-sm text-white/70">
                ₹{selectedEmployee.monthly_salary.toLocaleString('en-IN')}/month
              </div>
              {isPrevious && (
                <div className="text-xs text-red-300 mt-1">
                  Left: {(() => {
                    const [year, month, day] = selectedEmployee.end_date.split('-').map(Number);
                    return new Date(year, month - 1, day).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
                  })()}
                </div>
              )}
            </div>
          </div>
          
          {/* Salary Summary for selected employee */}
          {salaryInfo && (
            <div className="mt-3 pt-3 border-t border-white/20">
              <div className="grid grid-cols-2 gap-2 text-xs">
                <div className="text-center p-2 rounded bg-white/10">
                  <div className="text-white/60 text-[10px]">Base</div>
                  <div className="font-semibold text-white text-sm">
                    ₹{(salaryInfo.curr?.base_salary || 0).toLocaleString('en-IN')}
                  </div>
                </div>
                <div className="text-center p-2 rounded bg-white/10">
                  <div className="text-white/60 text-[10px]">OT</div>
                  <div className="font-semibold text-white text-sm">
                    ₹{(salaryInfo.curr?.overtime_payment || 0).toLocaleString('en-IN')}
                  </div>
                </div>
                <div className="text-center p-2 rounded bg-white/10">
                  <div className="text-white/60 text-[10px]">Remaining Advance</div>
                  <div className="font-semibold text-white text-sm">
                    ₹{(salaryInfo.curr?.available_advance || 0).toLocaleString('en-IN')}
                  </div>
                </div>
                <div className="text-center p-2 rounded bg-amber-500/30">
                  <div className="text-white/60 text-[10px]">Net</div>
                  <div className="font-bold text-amber-300 text-sm">
                    ₹{(salaryInfo.curr?.net_payment || 0).toLocaleString('en-IN')}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
        
        {isApprover && (
          <div className="absolute right-2 top-4">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="p-2 rounded-lg transition-colors hover:bg-white/20 text-white">
                  <MoreVertical className="w-4 h-4" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => onEditEmployee(selectedEmployee)} className="cursor-pointer">
                  <Edit2 className="w-4 h-4 mr-2" />
                  Edit
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => onDeleteEmployee(selectedEmployee.employee_id)} className="cursor-pointer text-red-600 focus:text-red-600">
                  <Trash2 className="w-4 h-4 mr-2" />
                  Delete
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        )}
      </div>
    );
  };

  return (
    <Card className="lg:col-span-1 p-5 border-0 rounded-2xl shadow-lg bg-white" data-testid="employee-list">
      <h3 className="text-xs font-bold text-slate-500 uppercase tracking-[0.15em] mb-4">Employees ({employees.length})</h3>
      
      {/* Select Employee Dropdown */}
      <div className="mb-4">
        <Select 
          value={selectedEmployee?.employee_id || ''} 
          onValueChange={handleSelectEmployee}
          data-testid="employee-select"
        >
          <SelectTrigger className="h-10 rounded-xl border-slate-200">
            <SelectValue placeholder="Select employee..." />
          </SelectTrigger>
          <SelectContent>
            {activeEmployees.length > 0 && (
              <SelectGroup>
                <SelectLabel className="text-green-600 font-semibold">Active Employees</SelectLabel>
                {activeEmployees.map(emp => (
                  <SelectItem key={emp.employee_id} value={emp.employee_id}>
                    <div className="flex items-center gap-2">
                      <User className="w-4 h-4 text-slate-400" />
                      <span>{emp.name}</span>
                      <span className="text-slate-400 text-xs">₹{emp.monthly_salary.toLocaleString('en-IN')}/mo</span>
                    </div>
                  </SelectItem>
                ))}
              </SelectGroup>
            )}
            {previousEmployees.length > 0 && (
              <SelectGroup>
                <SelectLabel className="text-slate-500 font-semibold">Previous Employees</SelectLabel>
                {previousEmployees.map(emp => (
                  <SelectItem key={emp.employee_id} value={emp.employee_id}>
                    <div className="flex items-center gap-2">
                      <User className="w-4 h-4 text-slate-300" />
                      <span className="text-slate-500">{emp.name}</span>
                      <span className="text-slate-400 text-xs">(Left)</span>
                    </div>
                  </SelectItem>
                ))}
              </SelectGroup>
            )}
          </SelectContent>
        </Select>
      </div>
      
      {/* Selected Employee Card */}
      <div className="space-y-2">
        {selectedEmployee ? (
          renderSelectedEmployeeCard()
        ) : (
          <div className="text-center py-12 text-slate-400">
            <div className="w-16 h-16 bg-slate-100 rounded-2xl flex items-center justify-center mx-auto mb-3">
              <Users className="w-8 h-8 text-slate-300" />
            </div>
            <p className="text-sm font-medium">Select an employee</p>
            <p className="text-xs mt-1">Choose from the dropdown above</p>
          </div>
        )}
      </div>
    </Card>
  );
}

export function EmptyEmployeeDetails() {
  return (
    <Card className="lg:col-span-2 p-6 border-0 rounded-2xl shadow-lg bg-white flex items-center justify-center">
      <div className="text-center py-16">
        <div className="w-20 h-20 bg-gradient-to-br from-slate-100 to-slate-200 rounded-2xl flex items-center justify-center mx-auto mb-4">
          <Users className="w-10 h-10 text-slate-400" />
        </div>
        <p className="text-slate-600 font-semibold text-lg">Select an employee</p>
        <p className="text-slate-400 text-sm mt-1">Choose from the list to view details</p>
      </div>
    </Card>
  );
}
